'use client';

import { navigation } from '@/constants';
import { Box } from '@mui/material';
import { useTranslate } from '@tolgee/react';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import LanguageSwitcher from './LanguageSwitcher';
import UserAvatar from './UserAvatar';
import { LogoZh } from './icons/LogoZh';
import { LogoEn } from './icons/logoEn';

const Header = () => {
  const { t } = useTranslate();
  const [scrolled, setScrolled] = useState(false);
  const locale = useLocale();

  // 统一的导航处理函数
  const navigateTo = useCallback(
    (path = '') => {
      const cleanHost = window.location.host.replace('provider.', '');
      const localePath = locale === 'zh' ? '/zh' : '';
      window.location.href = `http://${cleanHost}${localePath}${path}`;
    },
    [locale],
  );

  // 头部操作按钮组件
  const HeaderActions = () => (
    <div className="flex items-center gap-3 ml-auto shrink-0">
      {/* 生图按钮 */}
      <button
        onClick={() => navigateTo('/workflows')}
        className="h-9 rounded-xl bg-purple-600 text-white transition-all [@media(hover:hover)]:hover:bg-purple-700 active:scale-95 select-none whitespace-nowrap px-2 text-xs font-semibold lg:px-4 lg:text-sm lg:font-medium"
      >
        {t('common.generate_images')}
      </button>

      {/* 语言切换器 */}
      <LanguageSwitcher />

      {/* 用户头像按钮 */}
      <UserAvatar />
    </div>
  );

  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 动态计算header样式
  const headerClasses = `fixed left-0 top-0 z-50 w-full border-b transition-all duration-300 
    ${scrolled ? 'border-n-6/50 bg-n-8/90 backdrop-blur-xl' : 'border-n-6/0 bg-n-8/50 backdrop-blur-sm'}`;

  return (
    <div className={headerClasses}>
      <div className="flex h-[70px] items-center px-3 max-lg:py-4 lg:px-7.5 xl:px-10">
        {/* Logo - 固定高度，宽度自适应 */}
        <button
          className="shrink-0 block h-10 cursor-pointer lg:ml-8 lg:mr-5"
          onClick={() => navigateTo('/')}
          type="button"
        >
          <Box
            component={locale === 'zh' ? LogoZh : LogoEn}
            alt="CrowdComputed Logo"
            className="h-7 lg:h-8"
            style={{
              width: 'auto',
              maxWidth: '160px',
              objectFit: 'contain',
            }}
          />
        </button>

        {/* Navigation - 动态自适应间距 */}
        <nav className="hidden lg:flex lg:h-full lg:flex-1">
          <div className="flex h-full w-full justify-center">
            <div className="flex items-center justify-around w-auto max-w-[600px] min-w-0 px-2">
              {navigation.map(
                (item) =>
                  !item.onlyMobile && (
                    <a
                      key={item.id}
                      href={item.url}
                      {...(item.external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      className="relative px-[min(1.25vw,20px)] py-2.5 mx-[min(0.2vw,4px)] text-center text-sm font-medium text-n-1/80 transition-colors [@media(hover:hover)]:hover:text-color-1 whitespace-nowrap"
                    >
                      {t(item.title)}
                      <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-color-1 opacity-0 transition-all duration-300 [@media(hover:hover)]:group-hover:w-full [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:hover:w-full [@media(hover:hover)]:hover:opacity-100"></span>
                    </a>
                  ),
              )}
            </div>
          </div>
        </nav>

        {/* 操作按钮 - 响应式右对齐 */}
        <HeaderActions />
      </div>
    </div>
  );
};

export default Header;
